import { useEffect, useCallback, useRef } from 'react';
import { useAppStore } from '../stores/appStore';
import wsService from '../services/websocket';
import { Event, ChatMessage } from '../types';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export const useWebSocket = () => {
  const { t } = useTranslation();
  const isConnected = useRef(false);
  
  const {
    addEvent,
    updateEvent,
    removeEvent,
    addChatMessage,
    setActyTyping,
    setConnectionStatus,
    setSettings,
  } = useAppStore();

  useEffect(() => {
    // Setup WebSocket event listeners
    const handleConnect = () => {
      isConnected.current = true;
      setConnectionStatus('online');
    };

    const handleDisconnect = () => {
      isConnected.current = false;
      setConnectionStatus('offline');
    };

    const handleReconnect = () => {
      isConnected.current = true;
      setConnectionStatus('online');
      toast.success(t('app.reconnecting'));
    };

    const handleSettingsUpdated = (updatedSettings: Record<string, any>) => {
      console.log('Settings updated via WebSocket:', updatedSettings);
      setSettings(updatedSettings);
    };

    // Event listeners
    wsService.on('connect', handleConnect);
    wsService.on('disconnect', handleDisconnect);
    wsService.on('reconnect', handleReconnect);
    wsService.on('settings:updated', handleSettingsUpdated);

    // Cleanup on unmount
    return () => {
      wsService.off('connect', handleConnect);
      wsService.off('disconnect', handleDisconnect);
      wsService.off('reconnect', handleReconnect);
      wsService.off('settings:updated', handleSettingsUpdated);
    };
  }, [setConnectionStatus, setSettings, t]);

  const sendChatMessage = useCallback((message: string) => {
    if (isConnected.current) {
      wsService.sendChatMessage(message);
    } else {
      toast.error(t('chat.offline'));
    }
  }, [t]);

  const joinEventRoom = useCallback((eventId: string) => {
    if (isConnected.current) {
      wsService.joinEventRoom(eventId);
    }
  }, []);

  const leaveEventRoom = useCallback((eventId: string) => {
    if (isConnected.current) {
      wsService.leaveEventRoom(eventId);
    }
  }, []);

  const startTyping = useCallback(() => {
    if (isConnected.current) {
      wsService.startTyping();
    }
  }, []);

  const stopTyping = useCallback(() => {
    if (isConnected.current) {
      wsService.stopTyping();
    }
  }, []);

  const requestEventUpdates = useCallback(() => {
    if (isConnected.current) {
      wsService.requestEventUpdates();
    }
  }, []);

  const getConnectionState = useCallback(() => {
    return wsService.getConnectionState();
  }, []);

  return {
    isConnected: isConnected.current,
    sendChatMessage,
    joinEventRoom,
    leaveEventRoom,
    startTyping,
    stopTyping,
    requestEventUpdates,
    getConnectionState,
    disconnect: wsService.disconnect.bind(wsService),
    reconnect: wsService.reconnect.bind(wsService),
  };
};

export const useChat = () => {
  const { t } = useTranslation();
  const {
    chatMessages,
    isActyTyping,
    isChatOpen,
    setChatOpen,
    addChatMessage,
    clearChat,
  } = useAppStore();
  
  const { sendChatMessage, startTyping, stopTyping, isConnected } = useWebSocket();

  const sendMessage = useCallback(async (message: string) => {
    if (!message.trim()) return;

    // Add user message immediately
    const userMessage: ChatMessage = {
      id: Date.now().toString(),
      content: message,
      sender: 'user',
      timestamp: new Date(),
    };
    
    addChatMessage(userMessage);

    // Send to WebSocket if connected
    if (isConnected) {
      sendChatMessage(message);
    } else {
      // Fallback to API if WebSocket is not connected
      try {
        const { chatAPI } = await import('../services/api');
        const response = await chatAPI.sendMessage(message);
        addChatMessage(response);
      } catch (error) {
        console.error('Failed to send chat message:', error);
        toast.error(t('errors.network'));
      }
    }
  }, [addChatMessage, sendChatMessage, isConnected, t]);

  const handleTypingStart = useCallback(() => {
    if (isConnected) {
      startTyping();
    }
  }, [startTyping, isConnected]);

  const handleTypingStop = useCallback(() => {
    if (isConnected) {
      stopTyping();
    }
  }, [stopTyping, isConnected]);

  const openChat = useCallback(() => {
    setChatOpen(true);
  }, [setChatOpen]);

  const closeChat = useCallback(() => {
    setChatOpen(false);
  }, [setChatOpen]);

  return {
    messages: chatMessages,
    isActyTyping,
    isChatOpen,
    isConnected,
    sendMessage,
    openChat,
    closeChat,
    clearChat,
    onTypingStart: handleTypingStart,
    onTypingStop: handleTypingStop,
  };
};

// Hook for handling WebSocket reconnection with exponential backoff
export const useWebSocketReconnection = () => {
  const reconnectAttempts = useRef(0);
  const maxAttempts = 5;
  const baseDelay = 1000;

  const { setConnectionStatus } = useAppStore();

  const reconnect = useCallback(async () => {
    if (reconnectAttempts.current >= maxAttempts) {
      setConnectionStatus('offline');
      return false;
    }

    const delay = baseDelay * Math.pow(2, reconnectAttempts.current);
    reconnectAttempts.current++;

    setConnectionStatus('reconnecting');

    return new Promise<boolean>((resolve) => {
      setTimeout(() => {
        try {
          wsService.reconnect();
          
          // Check if reconnection was successful after a short delay
          setTimeout(() => {
            if (wsService.isConnected()) {
              reconnectAttempts.current = 0;
              setConnectionStatus('online');
              resolve(true);
            } else {
              resolve(false);
            }
          }, 1000);
        } catch (error) {
          console.error('Reconnection failed:', error);
          resolve(false);
        }
      }, delay);
    });
  }, [setConnectionStatus]);

  const resetReconnectAttempts = useCallback(() => {
    reconnectAttempts.current = 0;
  }, []);

  return {
    reconnect,
    resetReconnectAttempts,
    reconnectAttempts: reconnectAttempts.current,
    maxAttempts,
  };
};

// Hook for handling real-time event updates
export const useRealTimeEvents = () => {
  const { updateEvent, addEvent, removeEvent } = useAppStore();

  useEffect(() => {
    const handleEventUpdate = (event: Event) => {
      updateEvent(event.id, event);
    };

    const handleEventCreated = (event: Event) => {
      addEvent(event);
    };

    const handleEventDeleted = (eventId: string) => {
      removeEvent(eventId);
    };

    // Subscribe to real-time events
    wsService.on('event:updated', handleEventUpdate);
    wsService.on('event:created', handleEventCreated);
    wsService.on('event:deleted', handleEventDeleted);

    return () => {
      wsService.off('event:updated', handleEventUpdate);
      wsService.off('event:created', handleEventCreated);
      wsService.off('event:deleted', handleEventDeleted);
    };
  }, [updateEvent, addEvent, removeEvent]);

  return {
    isConnected: wsService.isConnected(),
  };
};